import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  faTrashCan,
  faClockRotateLeft,
} from "@fortawesome/pro-solid-svg-icons";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import {
  AdminListCard,
  PageTabs,
  AdminListTable,
  ConfirmDeleteModal,
  useArchiveFilter,
  useBatchSelection,
  type BatchAction,
} from "@components/CatalogIndex";
import { useGovernancesList } from "../api/useGovernancesList";
import { useGovernanceMutations } from "../api/useGovernanceMutations";
import { governanceColumns } from "./columns";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";

type PendingAction = "archive" | "restore" | "purge" | null;

export const List = () => {
  const navigate = useNavigate();
  const { tab, setTab, isArchivedFilter, tabsFor } = useArchiveFilter();
  const { rows, totalCount, isLoading, isFetching, refetch } =
    useGovernancesList({
      archived: isArchivedFilter,
    });
  const archivedQuery = useGovernancesList({ archived: !isArchivedFilter });
  const { destroy, move, restore, purge } = useGovernanceMutations();

  const { selectedIds, setSelectedIds, clear } = useBatchSelection([tab]);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [toast, setToast] = useState<{
    variant: "success" | "error";
    text: string;
  } | null>(null);

  if (isLoading) return <ScopeStackSpinner />;

  const runBatch = async (
    fn: (id: string) => Promise<unknown>,
    successText: string,
    failText: string
  ) => {
    const ids = [...selectedIds];
    let failures = 0;
    for (const id of ids) {
      try {
        await fn(id);
      } catch {
        failures += 1;
      }
    }
    if (failures === 0) {
      setToast({
        variant: "success",
        text: successText.replace("{count}", String(ids.length)),
      });
    } else {
      setToast({
        variant: "error",
        text: failText
          .replace("{failures}", String(failures))
          .replace("{count}", String(ids.length)),
      });
    }
    clear();
    setPendingAction(null);
    // Mutations don't invalidate the list cache, and an archive/restore/purge
    // moves rows between tabs, so refresh both.
    refetch();
    archivedQuery.refetch();
  };

  const handleConfirm = async () => {
    if (pendingAction === "archive") {
      await runBatch(
        (id) => destroy(id),
        "{count} governance item(s) archived.",
        "Failed to archive {failures} of {count} items."
      );
    } else if (pendingAction === "restore") {
      await runBatch(
        (id) => restore(id),
        "{count} governance item(s) restored.",
        "Failed to restore {failures} of {count} items."
      );
    } else if (pendingAction === "purge") {
      await runBatch(
        (id) => purge(id),
        "{count} governance item(s) permanently deleted.",
        "Failed to permanently delete {failures} of {count} items."
      );
    }
  };

  const handleReorder = async (
    moves: Array<{ id: string | number; position: number }>
  ) => {
    // Serialize: the API's set_list_position! cascades neighbor shifts on
    // each call, so parallel moves race for the unique-position window.
    // Moves arrive sorted by ascending target position, which lets
    // acts_as_list compact sparse positions into 1..N.
    try {
      for (const { id, position } of moves) {
        await move(String(id), position);
      }
    } catch {
      setToast({
        variant: "error",
        text: "Failed to reorder. The list has been refreshed.",
      });
    } finally {
      // The move mutations aren't tag-invalidating, so the list query cache
      // still holds the pre-reorder positions. Refetch so the new order
      // survives remounts (e.g. switching tabs) — the optimistic AdminListTable
      // state alone is discarded on unmount.
      refetch();
    }
  };

  const counts = isArchivedFilter
    ? { active: archivedQuery.totalCount, archived: totalCount }
    : { active: totalCount, archived: archivedQuery.totalCount };

  const activeActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Archive",
      onClick: () => setPendingAction("archive"),
    },
  ];

  const archivedActions: BatchAction[] = [
    {
      icon: faClockRotateLeft,
      label: "Restore",
      onClick: () => setPendingAction("restore"),
    },
    {
      icon: faTrashCan,
      label: "Permanently delete",
      onClick: () => setPendingAction("purge"),
      variant: "danger",
    },
  ];

  const batchActions = isArchivedFilter ? archivedActions : activeActions;

  return (
    <>
      <AdminListCard
        title="Project Governances"
        onNew={() => navigate("./new")}
        newButtonLabel="+ Add Governance"
        batchActions={batchActions}
        selectedCount={selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => setTab(key as "active" | "archived")}
          tabs={tabsFor(counts)}
        />
        <AdminListTable
          columns={governanceColumns}
          data={rows}
          isLoading={isFetching}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          draggable={!isArchivedFilter}
          onReorder={!isArchivedFilter ? handleReorder : undefined}
          onEditHref={(row) => `/admin/governances/${row.id}/edit`}
          editLinkDisabled={isArchivedFilter}
          emptyMessage={
            isArchivedFilter
              ? "No archived governance items."
              : "No governance items yet — add one to get started."
          }
        />
      </AdminListCard>
      <ConfirmDeleteModal
        open={pendingAction === "archive"}
        title={`Delete governance? (${selectedIds.length})`}
        message={`Move ${selectedIds.length} governance item(s) to Archived?`}
        onConfirm={handleConfirm}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmDeleteModal
        open={pendingAction === "restore"}
        title={`Restore governance? (${selectedIds.length})`}
        message={`Restore ${selectedIds.length} governance item(s) to Active?`}
        confirmLabel="Restore"
        onConfirm={handleConfirm}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmDeleteModal
        open={pendingAction === "purge"}
        title={`Permanently delete governance? (${selectedIds.length})`}
        message={`Permanently delete ${selectedIds.length} governance item(s)? This cannot be undone.`}
        confirmLabel="Permanently Delete"
        onConfirm={handleConfirm}
        onCancel={() => setPendingAction(null)}
      />
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
